import {
  getDefaultCollectionViewData,
  getDefaultProductViewData,
  transformCartToTrackingData,
  waitForSelector,
} from '@/scripts/tracking/helper'
import type { Cart } from '@/types/shopify'
import type { GTMTrackingCartData, GTMTrackingItemData } from '@/types/tracking'

export const waitForElevarDependencies = async () => {
  return await Promise.all([
    waitForSelector(
      document.head,
      '#elevar-gtm-suite-listener[data-ready=true]',
    ),
    waitForSelector(document.head, '#elevar-gtm-suite[data-ready=true]'),
  ])
}

export const sendInitialProductView = ({
  cart,
  data,
}: {
  cart?: GTMTrackingCartData
  data?: any
}) => {
  const productData = getDefaultProductViewData() || data

  productData &&
    window.ElevarGtmSuite?.handlers?.productView({
      attributes: cart?.attributes,
      currencyCode: cart?.currency || 'USD',
      items: [productData],
    })

  productData &&
    window?.dataLayer?.push({
      event: 'view_item',
      ecommerce_shopify: {
        currency: cart?.currency || 'USD',
        value: Number(productData.price),
        items: [productData],
      },
    })
}

export const sendInitialCollectionView = async (cart: GTMTrackingCartData) => {
  const collectionData = getDefaultCollectionViewData()
  const collectionViewPayload = {
    attributes: cart.attributes,
    currencyCode: cart.currency,
    items: collectionData,
  }

  if (
    Array.isArray(collectionData) &&
    collectionData.length &&
    window.ElevarGtmSuite?.handlers
  ) {
    const products = await window.ElevarGtmSuite.handlers.collectionView(
      collectionViewPayload,
    )
    window.ElevarGtmSuite?.handlers?.productSelect(
      collectionViewPayload,
      products,
    )
  }
}

export const sendCartUpdateEvent = (cart: Cart) => {
  const trackingCartData = transformCartToTrackingData(cart)
  window.ElevarGtmSuite?.handlers?.cartItemsReconcile(trackingCartData)
}

export const pushAddToCartEvent = (cart: Cart, item: GTMTrackingItemData) => {
  const currency = cart?.cost?.totalAmount?.currencyCode || 'USD'

  window?.dataLayer?.push({
    event: 'add_to_cart',
    ecommerce_shopify: {
      currency,
      value: Number(item.price),
      items: [item],
    },
  })
}

export const pushRemoveFromCartEvent = (
  cart: Cart,
  item: GTMTrackingItemData,
) => {
  const currency = cart?.cost?.totalAmount?.currencyCode || 'USD'

  window?.dataLayer?.push({
    event: 'remove_from_cart',
    ecommerce_shopify: {
      currency,
      value: Number(item.price),
      items: [item],
    },
  })
}

export const pushSelectItemEvent = (item: GTMTrackingItemData) => {
  window?.dataLayer?.push({
    event: 'select_item',
    ecommerce_shopify: {
      items: [item],
    },
  })
}

export const pushViewItemEvent = (
  activeVariantPrice: string,
  item: GTMTrackingItemData,
) => {
  window?.dataLayer?.push({
    event: 'view_item',
    ecommerce_shopify: {
      value: activeVariantPrice,
      items: [item],
    },
  })
}

export const pushAddToWishlistEvent = (
  activeVariantPrice: string,
  item: GTMTrackingItemData,
) => {
  window?.dataLayer?.push({
    event: 'add_to_wishlist',
    ecommerce_shopify: {
      value: activeVariantPrice,
      items: [item],
    },
  })
}

export const pushViewItemListEvent = (items: GTMTrackingItemData[]) => {
  window.dataLayer?.push({
    event: 'view_item_list',
    ecommerce_shopify: {
      items,
    },
  })
}

export const pushMarketingSignupEvent = (method: string, place: string) => {
  window?.dataLayer?.push({
    event: 'marketing_signup',
    ecommerce_shopify: {
      method: method,
      place: place,
    },
  })
}

export const pushSearchEvent = (searchTerm: string) => {
  window?.dataLayer?.push({
    event: 'search',
    ecommerce_shopify: {
      search_term: searchTerm,
    },
  })
}

export const pushLoginEvent = (userId?: number) => {
  window?.dataLayer?.push({
    event: 'login',
    ecommerce_shopify: {
      user_id: userId,
    },
  })
}

export const pushViewCartEvent = (cart: GTMTrackingCartData) => {
  window.dataLayer?.push({
    event: 'view_cart',
    ecommerce_shopify: {
      value: cart.items_subtotal_price,
      items: cart.items,
    },
  })
}
